import React from 'react';

import AnalyticsIcon from '@mui/icons-material/AnalyticsRounded';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonthRounded';
import ChecklistIcon from '@mui/icons-material/ChecklistRounded';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmarkRounded';
import Diversity2Icon from '@mui/icons-material/Diversity2Rounded';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrowRounded';
import GradingIcon from '@mui/icons-material/Grading';
import HomeIcon from '@mui/icons-material/HomeRounded';
import Inventory2Icon from '@mui/icons-material/Inventory2Rounded';
import LibraryAddIcon from '@mui/icons-material/LibraryAddCheckRounded';
import LockPersonIcon from '@mui/icons-material/LockPersonRounded';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOnRounded';
import PeopleIcon from '@mui/icons-material/PeopleAltRounded';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SchoolIcon from '@mui/icons-material/SchoolRounded';
import TimelineIcon from '@mui/icons-material/TimelineRounded';
import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFrontRounded';
import VideoLibraryIcon from '@mui/icons-material/VideoLibraryRounded';
import ViewModuleIcon from '@mui/icons-material/ViewModuleRounded';
import WorkspacesIcon from '@mui/icons-material/WorkspacesRounded';
import {
  ListItem, ListItemIcon, useTheme,
} from '@mui/material';
import { DrawerProps as MUIDrawerProps } from '@mui/material/Drawer';
import _ from 'lodash';
import {
  useLocation,
} from 'react-router-dom';

import { UserGroup } from '../../../types/common';
import Link from '../../Link';
import ListItemButtonLink from '../../ListItemButtonLink';
import { ColorHex } from '../../ProfileIcon/types';
import ProfileButton from '../ProfileButton';
import { hasOverlap, isInPaths } from '../utils';

import {
  GradientEmphasis,
  ListHeaderText,
  ListItemText,
  ScrollableContainer,
  StyledDrawer,
  StyledList,
  Title,
  TitleToolbar,
} from './index.styles';

interface MenuOptionIconProps {
  selected: boolean,
  children: React.ReactElement,
}

const MenuOptionIcon = ({ children, selected }: MenuOptionIconProps) =>
  React.cloneElement(children, { color: (selected ? 'darkPrimary' : '') });

interface MenuOption {
  icon: React.ReactElement,
  name: string,
  href: string,
  // List of endpoints which should result in the menu option being marked as selected
  linkedEndpoints: string[]
}

/**
 * Dashboard Menus
 */

const dashboardMenu: MenuOption[] = [
  {
    icon: <HomeIcon />,
    name: 'Dashboard',
    href: '/',
    linkedEndpoints: [''],
  },
];

const managerDashboardMenu: MenuOption[] = [
  {
    icon: <HomeIcon />,
    name: 'Dashboard',
    href: '/',
    linkedEndpoints: [''],
  },
];

const adminDashboardMenu: MenuOption[] = [
  {
    icon: <HomeIcon />,
    name: 'Dashboard',
    href: '/',
    linkedEndpoints: [''],
  },
  {
    icon: <ChecklistIcon />,
    name: 'Roll',
    href: '/roll',
    linkedEndpoints: ['/roll'],
  },
  {
    icon: <DoubleArrowIcon />,
    name: 'Follow Up',
    href: '/admin/follow-up',
    linkedEndpoints: ['admin/follow-up'],
  },
];

const teacherDashboardMenu: MenuOption[] = [
  {
    icon: <HomeIcon />,
    name: 'Dashboard',
    href: '/',
    linkedEndpoints: [''],
  },
  {
    icon: <ChecklistIcon />,
    name: 'Roll',
    href: '/roll',
    linkedEndpoints: ['/roll'],
  },
];

/**
 * Service Menus
 */

const adminServiceMenu: MenuOption[] = [
  {
    icon: <ViewModuleIcon />,
    name: 'Subjects',
    href: '/admin/subjects',
    linkedEndpoints: [
      'admin/subjects',
      'admin/subject',
      'admin/topics',
      'admin/topic',
      'admin/courses',
      'admin/course',
      'admin/streams',
      'admin/stream',
      'admin/lessons',
      'admin/lesson',
      'admin/notes',
      'admin/note',
      'admin/videos',
      'admin/video',
      'admin/resources',
      'admin/resource',
      'admin/quizzes',
      'admin/quiz',
      'admin/exams',
      'admin/exam',
    ],
  },
  {
    icon: <Diversity2Icon />,
    name: 'Classes',
    href: '/admin/classes',
    linkedEndpoints: ['admin/classes', 'admin/class'],
  },
  {
    icon: <VideoCameraFrontIcon />,
    name: 'Tutorials',
    href: '/tutorials',
    linkedEndpoints: ['tutorials'],
  },
];

const teacherServiceMenu: MenuOption[] = [
  {
    icon: <Diversity2Icon />,
    name: 'Classes',
    href: '/teacher/classes',
    linkedEndpoints: ['teacher/classes', 'teacher/class'],
  },
  {
    icon: <VideoCameraFrontIcon />,
    name: 'Tutorials',
    href: '/tutorials',
    linkedEndpoints: ['tutorials'],
  },
];

/**
 * Content Menus
 */

const contentMenu: MenuOption[] = [
  {
    icon: <CollectionsBookmarkIcon />,
    name: 'Notes',
    href: '/notes',
    linkedEndpoints: ['notes'],
  },
  {
    icon: <VideoLibraryIcon />,
    name: 'Videos',
    href: '/videos',
    linkedEndpoints: ['videos', 'video'],
  },
];

const teacherContentMenu: MenuOption[] = [
  {
    icon: <CollectionsBookmarkIcon />,
    name: 'Notes',
    href: '/notes',
    linkedEndpoints: ['notes'],
  },
  {
    icon: <VideoLibraryIcon />,
    name: 'Videos',
    href: '/videos',
    linkedEndpoints: ['videos', 'video'],
  },
  {
    icon: <LibraryAddIcon />,
    name: 'Exams',
    href: '/exams',
    linkedEndpoints: ['exams'],
  },
  // {
  //   icon: <LibraryBooksIcon />,
  //   name: 'Resources',
  //   href: '/resources',
  //   linkedEndpoints: ['resources'],
  // },
  // {
  //   icon: <QuizIcon />,
  //   name: 'Quizzes',
  //   href: '/quizzes',
  //   linkedEndpoints: ['quizzes'],
  // },
];

/**
 * Tools Menus
 */

const toolsMenu: MenuOption[] = [
  {
    icon: <VideoCameraFrontIcon />,
    name: 'Tutorials',
    href: '/tutorials',
    linkedEndpoints: ['tutorials'],
  },
  {
    icon: <AnalyticsIcon />,
    name: 'Analytics',
    href: '/analytics',
    linkedEndpoints: ['analytics'],
  },
];

const managerToolsMenu: MenuOption[] = [
  {
    icon: <MonetizationOnIcon />,
    name: 'Payroll',
    href: '/admin/payroll',
    linkedEndpoints: ['admin/payroll'],
  },
  {
    icon: <CalendarMonthIcon />,
    name: 'Timesheet',
    href: '/admin/timesheet',
    linkedEndpoints: ['admin/timesheet'],
  },
  {
    icon: <Inventory2Icon />,
    name: 'Inventory',
    href: '/admin/inventory',
    linkedEndpoints: ['admin/inventory'],
  },
  {
    icon: <TimelineIcon />,
    name: 'Analytics',
    href: '/admin/analytics',
    linkedEndpoints: ['admin/analytics'],
  },
];

const adminToolsMenu: MenuOption[] = [
  {
    icon: <Inventory2Icon />,
    name: 'Inventory',
    href: '/admin/inventory',
    linkedEndpoints: ['admin/inventory'],
  },
  {
    icon: <ReceiptIcon />,
    name: 'Invoices',
    href: '/admin/invoices',
    linkedEndpoints: ['admin/invoices'],
  },
  {
    icon: <PrintIcon />,
    name: 'Printing',
    href: '/admin/printing',
    linkedEndpoints: ['admin/printing'],
  },
  {
    icon: <GradingIcon />,
    name: 'Exam Assignment',
    href: '/admin/exam-assignment',
    linkedEndpoints: ['admin/exam-assignment'],
  },
];

const teacherToolsMenu: MenuOption[] = [
  {
    icon: <GradingIcon />,
    name: 'Marking',
    href: '/teacher/marking',
    linkedEndpoints: ['teacher/marking'],
  },
  {
    icon: <GradingIcon />,
    name: 'Marking (Legacy)',
    href: '/teacher/legacy-marking',
    linkedEndpoints: ['teacher/legacy-marking'],
  },
];

/**
 * Admin Exclusive Menus
 */

const adminUsersMenu: MenuOption[] = [
  {
    icon: <SchoolIcon />,
    name: 'Students',
    href: '/admin/students',
    linkedEndpoints: ['admin/students', 'admin/student'],
  },
  {
    icon: <PeopleIcon />,
    name: 'Staff',
    href: '/admin/staff',
    linkedEndpoints: ['admin/staff'],
  },
  {
    icon: <PermContactCalendarIcon />,
    name: 'Enrolments',
    href: '/admin/leads',
    linkedEndpoints: ['admin/leads', 'admin/lead'],
  },
];

/**
 * Manager Exclusive Menus
 */

const managerAccessMenu: MenuOption[] = [
  {
    icon: <LockPersonIcon />,
    name: 'Permissions',
    href: '/admin/permissions',
    linkedEndpoints: ['admin/permissions', 'admin/permission'],
  },
  {
    icon: <WorkspacesIcon />,
    name: 'Groups',
    href: '/admin/groups',
    linkedEndpoints: ['admin/groups'],
  },
];

interface MenuListProps {
  source: MenuOption[],
  header?: string | undefined,
  currentPath?: string | undefined,
}

const MenuList = ({ header, source, currentPath = '' }: MenuListProps) => {
  const theme = useTheme();

  return (
    <StyledList>
      {header && (
        <ListItem>
          <ListHeaderText
            primary={header}
            primaryTypographyProps={{ variant: 'overline' }}
          />
        </ListItem>
      )}
      {source.map((item) => (
        <ListItemButtonLink
          selected={isInPaths(item.linkedEndpoints, currentPath)}
          key={item.name}
          href={item.href}
          disableRipple
          // In-line styling as the component prop is not forwarded to
          // styled-components
          style={{
            margin: `${theme.spacing(1)} 0`,
          }}
        >
          <ListItemIcon>
            <MenuOptionIcon
              selected={isInPaths(item.linkedEndpoints, currentPath)}
            >
              {item.icon}
            </MenuOptionIcon>
          </ListItemIcon>
          <ListItemText
            primary={item.name}
            primaryTypographyProps={{ variant: 'body2' }}
            selected={isInPaths(item.linkedEndpoints, currentPath)}
          />
        </ListItemButtonLink>
      ))}
    </StyledList>
  );
};

interface MenuProps {
  currentPath?: string | undefined,
  userGroups: string[],
}

const Menu = ({ currentPath, userGroups }: MenuProps) => {
  const { pathname } = useLocation();

  const isAdmin = hasOverlap(userGroups, [UserGroup.Admins]);
  const isManager = hasOverlap(userGroups, [UserGroup.Managers]);
  const isTeacher = hasOverlap(userGroups, [UserGroup.Teachers]);
  const isStudent = hasOverlap(userGroups, [UserGroup.Students]);

  return (
    <>
      {(isStudent) && (
        <>
          <MenuList source={dashboardMenu} currentPath={currentPath || pathname} />
          <MenuList header="Study" source={contentMenu} currentPath={currentPath || pathname} />
          <MenuList header="Tools" source={toolsMenu} currentPath={currentPath || pathname} />
        </>
      )}
      {(isManager || isAdmin || isTeacher) && (
        <MenuList
          source={_.uniqBy([
            ...(isManager ? managerDashboardMenu : []),
            ...(isAdmin ? adminDashboardMenu : []),
            ...(isTeacher ? teacherDashboardMenu : []),
          ], 'name')}
          currentPath={currentPath || pathname}
        />
      )}
      {isAdmin && (
        <MenuList header="Users" source={adminUsersMenu} currentPath={currentPath || pathname} />
      )}
      {(isAdmin || isTeacher) && (
        <MenuList
          header="Service"
          source={_.uniqBy([
            ...(isAdmin ? adminServiceMenu : []),
            ...(isTeacher ? teacherServiceMenu : []),
          ], 'name')}
          currentPath={currentPath || pathname}
        />
      )}
      {isTeacher && (
        <MenuList
          header="Content"
          source={_.uniqBy([
            ...(isTeacher ? teacherContentMenu : []),
          ], 'name')}
          currentPath={currentPath || pathname}
        />
      )}
      {isManager && (
        <MenuList header="Access" source={managerAccessMenu} currentPath={currentPath || pathname} />
      )}
      {(isManager || isAdmin || isTeacher) && (
        <MenuList
          header="Tools"
          source={_.uniqBy([
            ...(isManager ? managerToolsMenu : []),
            ...(isAdmin ? adminToolsMenu : []),
            ...(isTeacher ? teacherToolsMenu : []),
          ], 'name')}
          currentPath={currentPath || pathname}
        />
      )}
    </>
  );
};

interface DrawerProps extends MUIDrawerProps {
  mode?: 'small' | 'medium',
  onClose?: () => void,
  userGroups?: string[],
  firstName?: string,
  lastName?: string,
  profileColor?: ColorHex,
  profileImage?: string,
}

const Drawer = ({ mode = 'medium',
  onClose = () => {},
  userGroups = [],
  firstName,
  lastName,
  profileColor,
  profileImage,
  ...rest }: DrawerProps) =>
  (
    <StyledDrawer
      ModalProps={{
      // Keep Drawer mounted on mobile
        keepMounted: mode === 'small',
      }}
      SlideProps={{
        appear: false,
      }}
      variant={mode === 'small' ? 'temporary' : 'persistent'}
      onClose={onClose}
      {...rest}
    >
      <TitleToolbar>
        <Title variant="h5">
          <Link href="/" underline="none" color="inherit">
            <GradientEmphasis>Cognito</GradientEmphasis> Online
          </Link>
        </Title>
      </TitleToolbar>
      <ScrollableContainer
        {...(mode === 'small' ? { onClick: onClose, onKeyDown: onClose } : {})}
      >
        <Menu userGroups={userGroups} />
      </ScrollableContainer>
      <ProfileButton
        onCloseDrawer={onClose}
        firstName={firstName}
        lastName={lastName}
        profileColor={profileColor}
        profileImage={profileImage}
      />
    </StyledDrawer>
  );

export default Drawer;
